import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart3, Users, User, Brain, FileText, Lightbulb, Trophy, Award, Activity, FileSearch, RefreshCw, Filter } from 'lucide-react';
import ExecutiveTab from '../components/intelligence/ExecutiveTab';
import TeamTab from '../components/intelligence/TeamTab';
import IndividualTab from '../components/intelligence/IndividualTab';
import CoachingTab from '../components/intelligence/CoachingTab';
import ReportsTab from '../components/intelligence/ReportsTab';
import SalesFunnelTab from '../components/intelligence/SalesFunnelTab';
import AdvancedTab from '../components/intelligence/AdvancedTab';
import GamificationTab from '../components/intelligence/GamificationTab';
import CallerIntelTab from '../components/intelligence/CallerIntelTab';
import MyCoachingTab from '../components/intelligence/MyCoachingTab';
import CoachingCard from '../components/CoachingCard';
import TeamPulseTab from '../components/intelligence/TeamPulseTab';
import ReportBuilderTab from '../components/intelligence/ReportBuilderTab';
import GlobalRevivableDealsTab from '../components/intelligence/GlobalRevivableDealsTab';
import { GradingBanner } from './admin/domain/calls/GradingBanner';
import { OperationalSearchButton } from '../components/search/OperationalSearchModal';
import { SendSmsButton } from '../components/custom/SendSmsButton';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Poll-driven grading banner for the tenant portal. Fetches the same
// domain-grading-status the master admin sees, but scoped to the
// currently logged-in tenant's own domain_id (auth is enforced by the
// backend's require_domain_access dependency).
const TenantGradingBanner = () => {
  const { user, token } = useAuth();
  const domainId = user?.domain_id;
  // Operational/admin tooling — manager roles only. Agents never see the
  // auto-grade toggle, progress line, or error panels, and their browsers
  // never even call the grading-status endpoint (role-gated fetch below).
  const isManager = MANAGER_ROLES.has(user?.role);
  const [gradingStatus, setGradingStatus] = useState(null);
  const [grading, setGrading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [autoGrade, setAutoGrade] = useState(true);
  const [togglingAutoGrade, setTogglingAutoGrade] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchStatus = useCallback(async () => {
    if (!domainId || !isManager) return;
    try {
      const r = await axios.get(`${API}/admin/domains/${domainId}/grading-status`, { headers });
      setGradingStatus(r.data);
      setPendingCount(r.data?.pending || 0);
      if (typeof r.data?.auto_grade_enabled === 'boolean') setAutoGrade(r.data.auto_grade_enabled);
    } catch (err) {
      // Silent — banner just won't render if the fetch fails.
      console.debug('TenantGradingBanner status fetch failed', err?.message);
    }
  }, [domainId, headers]);

  useEffect(() => {
    if (!isManager) return undefined;
    fetchStatus();
    const id = setInterval(fetchStatus, 15000);
    return () => clearInterval(id);
  }, [fetchStatus, isManager]);

  const handleGradePending = async () => {
    if (!domainId) return;
    const count = gradingStatus?.gradeable || pendingCount;
    if (!window.confirm(`Fetch transcripts and AI-grade ${count} pending calls?`)) return;
    setGrading(true);
    try {
      const r = await axios.post(`${API}/admin/domains/${domainId}/grade-pending`, {}, { headers });
      toast.success(r.data.pending === 0 ? r.data.message : `${r.data.message} Check back in ~2 mins.`);
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start grading');
      console.error('TenantGradingBanner grade error', err);
    } finally {
      setGrading(false);
    }
  };

  const handleToggleAutoGrade = async () => {
    if (!domainId || togglingAutoGrade) return;
    const next = !autoGrade;
    setTogglingAutoGrade(true);
    try {
      await axios.patch(`${API}/admin/domains/${domainId}/auto-grade`, { enabled: next }, { headers });
      setAutoGrade(next);
      toast.success(next
        ? 'Auto-grade ON — new calls will grade automatically every 5 minutes.'
        : 'Auto-grade OFF — click "Grade All Pending" to grade manually.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update auto-grade setting');
    } finally {
      setTogglingAutoGrade(false);
    }
  };

  // Hide the banner when there is genuinely nothing to grade AND no
  // pending activity — mirrors the master-admin behaviour.
  const showBanner = (pendingCount > 0) || (gradingStatus?.gradeable > 0) || (gradingStatus?.error_count > 0);
  if (!domainId || !isManager) return null;

  return (
    <div className="space-y-2">
      {/* Auto-grade toggle pill — always visible so tenants can flip it any time */}
      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm">
        <div className="flex items-center gap-2 text-slate-600">
          <span className={`inline-block w-2 h-2 rounded-full ${autoGrade ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
          <span className="font-medium text-slate-800">
            Auto-grade: {autoGrade ? 'ON' : 'OFF'}
          </span>
          <span className="text-slate-500">
            {autoGrade
              ? '— new calls grade automatically every 5 minutes'
              : '— you\u2019ll need to click "Grade All Pending" manually'}
          </span>
        </div>
        <button
          onClick={handleToggleAutoGrade}
          disabled={togglingAutoGrade}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${autoGrade ? 'bg-emerald-500' : 'bg-slate-300'}`}
          data-testid="auto-grade-toggle"
          aria-label="Toggle auto-grade"
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${autoGrade ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {showBanner && (
        <GradingBanner
          pendingCount={pendingCount}
          gradingStatus={gradingStatus}
          grading={grading}
          onGradePending={handleGradePending}
          onRetryFailed={async () => {
            try {
              const r = await axios.post(`${API}/admin/domains/${domainId}/retry-failed-gradings`, {}, { headers });
              toast.success(r.data.message || 'Retry queued');
              fetchStatus();
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Retry failed to start');
            }
          }}
        />
      )}
    </div>
  );
};

const TABS = [
  { value: 'executive', label: 'Executive', icon: BarChart3 },
  { value: 'team-pulse', label: 'Team Pulse', icon: Activity },
  { value: 'team', label: 'Team', icon: Users },
  { value: 'individual', label: 'Individual', icon: User },
  { value: 'intelligence', label: 'Intelligence', icon: Lightbulb },
  { value: 'revivable', label: 'Revivable Opps', icon: RefreshCw },
  { value: 'caller-intel', label: 'Caller Intel', icon: Brain },
  { value: 'my-coaching', label: 'My Coaching', icon: Award },
  { value: 'coaching', label: 'Coaching Queue', icon: Brain },
  { value: 'gamification', label: 'Achievements', icon: Trophy },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'sales-funnel', label: 'Sales Funnel', icon: Filter },
  { value: 'report-builder', label: 'Report Builder', icon: FileSearch },
];

// Whitelist approach — only these roles see the full admin tab set.
// Anyone else (including the many free-form Yabbit-imported roles like
// `user`, `sales_rep`, `agent_lead`, blank/missing role, etc.) gets the
// locked-down personal view. This is safer than trying to blacklist
// every possible agent-role string.
const MANAGER_ROLES = new Set([
  'master_admin',
  'admin',
  'manager',
  'supervisor',
  'domain_admin',
  'domain_supervisor',
]);
const AGENT_TAB_ORDER = [
  'individual',       // default landing
  'intelligence',
  'revivable',
  'caller-intel',
  'my-coaching',
  'coaching',
];

const Dashboard = () => {
  const { user } = useAuth();
  const isAgent = !MANAGER_ROLES.has(user?.role);
  const visibleTabs = React.useMemo(() => {
    if (!isAgent) return TABS;
    const allowed = new Set(AGENT_TAB_ORDER);
    return TABS
      .filter((t) => allowed.has(t.value))
      .sort((a, b) => AGENT_TAB_ORDER.indexOf(a.value) - AGENT_TAB_ORDER.indexOf(b.value));
  }, [isAgent]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Deep-link support — the Performance-by-Site card on the Exec tab
    // navigates to `?tab=revivable&site=X`. Read the tab up-front so the
    // initial render lands on the right tab (no flash).
    const q = searchParams.get('tab');
    return q || (isAgent ? 'individual' : 'executive');
  });

  // React to URL changes AFTER mount — click-throughs from other cards
  // (e.g. Performance-by-Site cell) update the URL via `navigate()`; the
  // Dashboard is already mounted so useState wouldn't re-fire. Watching
  // searchParams here syncs activeTab on every URL push.
  useEffect(() => {
    const q = searchParams.get('tab');
    if (q && q !== activeTab) setActiveTab(q);
  }, [searchParams, activeTab]);

  // Keep the URL in sync when the tab changes so a browser refresh
  // preserves the current view (and back/forward navigation works).
  useEffect(() => {
    if (searchParams.get('tab') === activeTab) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', activeTab);
      // Clear the site chip on manual tab change so switching away and
      // back doesn't restore a stale per-site filter.
      if (searchParams.get('tab') && searchParams.get('tab') !== activeTab) next.delete('site');
      return next;
    }, { replace: true });
  }, [activeTab]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <TenantGradingBanner />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>
            Yabbit VoiceFabric
          </h1>
          <p className="text-slate-500 text-sm">AI call grading, behavioural coaching and revenue intelligence for your entire team</p>
        </div>
        <div className="flex items-center gap-2">
          <SendSmsButton />
          <OperationalSearchButton data-testid="dashboard-operational-search" />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl h-auto flex-wrap gap-1" data-testid="dashboard-tabs">
          {visibleTabs.map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              // Active state: keep ink-dark text (semantic, always AA)
              // and paint the tenant accent as a bottom underline
              // instead. If --brand-accent is unset the fallback is
              // the platform's primary teal so a fresh tenant renders
              // as before this change.
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 data-[state=active]:border-b-2 border-transparent data-[state=active]:border-[color:var(--brand-accent,hsl(var(--primary)))]"
              data-testid={`tab-${value}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="executive" className="mt-6">
          <ExecutiveTab />
        </TabsContent>
        <TabsContent value="team" className="mt-6">
          <TeamTab />
        </TabsContent>
        <TabsContent value="individual" className="mt-6">
          <IndividualTab />
        </TabsContent>
        <TabsContent value="coaching" className="mt-6">
          <CoachingTab />
        </TabsContent>
        <TabsContent value="intelligence" className="mt-6">
          <AdvancedTab />
        </TabsContent>
        <TabsContent value="revivable" className="mt-6">
          <GlobalRevivableDealsTab />
        </TabsContent>
        <TabsContent value="caller-intel" className="mt-6">
          <CallerIntelTab />
        </TabsContent>
        <TabsContent value="my-coaching" className="mt-6">
          <div className="space-y-4">
            <CoachingCard />
            <MyCoachingTab />
          </div>
        </TabsContent>
        <TabsContent value="gamification" className="mt-6">
          <GamificationTab />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <ReportsTab />
        </TabsContent>
        <TabsContent value="sales-funnel" className="mt-6">
          <SalesFunnelTab />
        </TabsContent>
        <TabsContent value="team-pulse" className="mt-6">
          <TeamPulseTab />
        </TabsContent>
        <TabsContent value="report-builder" className="mt-6">
          <ReportBuilderTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
